import { createContext, useContext, useReducer, useCallback, useRef } from 'react';

const ToastContext = createContext(null);

function toastReducer(state, action) {
  switch (action.type) {
    case 'ADD':
      return [...state, action.toast];
    case 'REMOVE':
      return state.filter((t) => t.id !== action.id);
    default:
      return state;
  }
}

export function ToastProvider({ children }) {
  const [toasts, dispatch] = useReducer(toastReducer, []);
  const timersRef = useRef({});

  const removeToast = useCallback((id) => {
    clearTimeout(timersRef.current[id]);
    delete timersRef.current[id];
    dispatch({ type: 'REMOVE', id });
  }, []);

  const addToast = useCallback((message, type = 'success', duration = 3000) => {
    const id = Date.now() + Math.random();
    dispatch({ type: 'ADD', toast: { id, message, type } });
    timersRef.current[id] = setTimeout(() => removeToast(id), duration);
    return id;
  }, [removeToast]);

  const toast = Object.assign(
    (msg) => addToast(msg, 'success'),
    {
      success: (msg) => addToast(msg, 'success'),
      error: (msg) => addToast(msg, 'error', 5000),
      warning: (msg) => addToast(msg, 'warning', 4000),
      info: (msg) => addToast(msg, 'info'),
    }
  );

  return (
    <ToastContext.Provider value={{ toast, toasts, removeToast }}>
      {children}
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx.toast;
}

export function useToastState() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToastState must be used within ToastProvider');
  return { toasts: ctx.toasts, removeToast: ctx.removeToast };
}
